import { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { AlertTriangle, ExternalLink, LoaderCircle, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAppStore } from "../store/useAppStore";
import type { SupportReport } from "../types";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { db } from "../lib/firebase";

const ADMIN_EMAIL = "stephen.tech139@gmail.com";
const REPORT_STATUS_OPTIONS: SupportReport["status"][] = ["open", "triaged", "resolved"];

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function ProblemReports() {
  const navigate = useNavigate();
  const { userEmail } = useAppStore();
  const [reports, setReports] = useState<SupportReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUpdatingId, setIsUpdatingId] = useState<string | null>(null);
  const isAdmin = userEmail?.toLowerCase() === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    const reportsQuery = query(collection(db, "supportReports"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(
      reportsQuery,
      (snapshot) => {
        const nextReports: SupportReport[] = [];
        snapshot.forEach((reportDoc) => {
          nextReports.push(reportDoc.data() as SupportReport);
        });
        setReports(nextReports);
        setLoading(false);
      },
      (error) => {
        console.error(error);
        toast.error("Could not load problem reports");
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [isAdmin]);

  const summary = useMemo(() => {
    return {
      total: reports.length,
      open: reports.filter((report) => report.status === "open").length,
      triaged: reports.filter((report) => report.status === "triaged").length,
      resolved: reports.filter((report) => report.status === "resolved").length,
    };
  }, [reports]);

  const handleStatusChange = async (report: SupportReport, nextStatus: SupportReport["status"]) => {
    if (report.status === nextStatus) {
      return;
    }

    try {
      setIsUpdatingId(report.id);
      await updateDoc(doc(db, "supportReports", report.id), { status: nextStatus });
      toast.success(`Marked as ${nextStatus}`);
    } catch (error: any) {
      toast.error(error?.message || "Could not update report status");
    } finally {
      setIsUpdatingId(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 pb-10">
        <div className="flex bg-white border-b border-slate-200 items-center justify-between px-8 py-4 -mx-4 sm:-mx-8 -mt-4 sm:-mt-8 mb-6">
          <div>
            <h1 className="text-xl font-bold">Problem Reports</h1>
            <p className="text-sm text-slate-500">Internal triage dashboard</p>
          </div>
        </div>
        <Card className="border-amber-200 bg-amber-50/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-950">
              <ShieldAlert className="h-5 w-5" />
              Admin access only
            </CardTitle>
            <CardDescription>
              This view is limited to the account owner so problem reports stay private.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-amber-900">Sign in with the admin account to review submitted reports.</p>
            <Button onClick={() => navigate("/")}>Back to dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 pb-10">
      <div className="flex bg-white border-b border-slate-200 items-center justify-between px-8 py-4 -mx-4 sm:-mx-8 -mt-4 sm:-mt-8 mb-6">
        <div>
          <h1 className="text-xl font-bold">Problem Reports</h1>
          <p className="text-sm text-slate-500">Review user-submitted issues, triage them, and jump into Sentry when needed.</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/settings")}>
          Back to settings
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{summary.total}</div>
            <p className="text-sm text-slate-500">Total reports</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-amber-600">{summary.open}</div>
            <p className="text-sm text-slate-500">Open</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{summary.triaged}</div>
            <p className="text-sm text-slate-500">Triaged</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-emerald-600">{summary.resolved}</div>
            <p className="text-sm text-slate-500">Resolved</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Recent reports</CardTitle>
              <CardDescription>{loading ? "Loading reports…" : "Newest submissions appear first."}</CardDescription>
            </div>
            {loading && <LoaderCircle className="h-5 w-5 animate-spin text-slate-400" />}
          </div>
        </CardHeader>
        <CardContent>
          {!loading && reports.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              No problem reports yet.
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <div key={report.id} className="rounded-xl border bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={report.status === "open" ? "destructive" : report.status === "triaged" ? "secondary" : "default"}>
                          {report.status}
                        </Badge>
                        <Badge variant="outline">{report.category}</Badge>
                        <Badge variant="outline">{report.severity}</Badge>
                        <Badge
                          variant={report.emailNotificationStatus === "sent" ? "default" : report.emailNotificationStatus === "failed" ? "destructive" : "secondary"}
                        >
                          Email {report.emailNotificationStatus || "pending"}
                        </Badge>
                        <span className="text-xs text-slate-500">{formatDate(report.createdAt)}</span>
                      </div>

                      <div>
                        <h3 className="text-base font-semibold text-slate-900">{report.summary}</h3>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{report.details}</p>
                      </div>

                      <div className="grid gap-2 text-xs text-slate-500 sm:grid-cols-2 xl:grid-cols-4">
                        <p><span className="font-medium text-slate-700">User:</span> {report.userEmail || report.userId}</p>
                        <p><span className="font-medium text-slate-700">Plan:</span> {report.planId}</p>
                        <p><span className="font-medium text-slate-700">App version:</span> {report.appVersion}</p>
                        <p><span className="font-medium text-slate-700">Provider:</span> {report.activeAiProvider}</p>
                      </div>

                      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                          <AlertTriangle className="h-3 w-3" />
                          {report.pagePath}
                        </span>
                        {report.sentryEventId && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                            <ExternalLink className="h-3 w-3" />
                            Sentry event available
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="w-full max-w-sm space-y-3 lg:w-72">
                      <div className="grid gap-2">
                        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Update status</label>
                        <Select
                          value={report.status}
                          onValueChange={(value) => handleStatusChange(report, value as SupportReport["status"])}
                          disabled={isUpdatingId === report.id}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Change status" />
                          </SelectTrigger>
                          <SelectContent>
                            {REPORT_STATUS_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="rounded-lg border bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        <p><span className="font-medium text-slate-700">Workspace:</span> {report.businessName || "—"}</p>
                        <p className="mt-1 break-all"><span className="font-medium text-slate-700">Sentry event:</span> {report.sentryEventId || "none"}</p>
                        <p className="mt-1"><span className="font-medium text-slate-700">Email status:</span> {report.emailNotificationStatus || "pending"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
